import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Diff,
  Hunk,
  Decoration,
  parseDiff,
  markEdits,
  tokenize,
  getCollapsedLinesCountBetween,
  useSourceExpansion,
  type HunkData,
} from 'react-diff-view'
import 'react-diff-view/style/index.css'
import { EmptyState } from './EmptyState'
import { prettyPrintStateForDiff, buildUnifiedDiffText } from '../lib/auditDiff'

export interface AuditDiffViewerProps {
  beforeState: Record<string, unknown> | null
  afterState: Record<string, unknown> | null
}

/**
 * Renders a GitLab-style split diff of an audit entry's before/after config.
 *
 * Callers MUST pass `key={entry.id}` so switching the drawer to a different
 * entry fully remounts this component instead of carrying over stale
 * collapsed/expanded hunk state from the previous entry.
 */
export function AuditDiffViewer({ beforeState, afterState }: AuditDiffViewerProps) {
  const { t } = useTranslation()

  const beforeText = useMemo(() => prettyPrintStateForDiff(beforeState), [beforeState])
  const afterText = useMemo(() => prettyPrintStateForDiff(afterState), [afterState])

  const initialHunks = useMemo(() => {
    if (beforeState === null && afterState === null) return []
    const diffText = buildUnifiedDiffText(beforeText, afterText)
    return parseDiff(diffText)[0]?.hunks ?? []
  }, [beforeState, afterState, beforeText, afterText])

  // Reveal hidden context on demand from the full "before" text — context
  // lines are identical on both sides, and gap start/end below are OLD line
  // numbers, so the before text is the correct source to slice from.
  const [hunks, expandRange] = useSourceExpansion(initialHunks, beforeText)

  if (beforeState === null && afterState === null) {
    return (
      <EmptyState
        title={t('auditLog.drawer.noChanges.title')}
        description={t('auditLog.drawer.noChanges.description')}
      />
    )
  }

  if (hunks.length === 0) {
    return <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{t('auditLog.drawer.noVisibleDiff')}</div>
  }

  const tokens = tokenize(hunks, { highlight: false, enhancers: [markEdits(hunks)] })
  const totalOldLines = beforeText === '' ? 0 : beforeText.split('\n').length

  function renderGap(previousHunk: HunkData | null, currentHunk: HunkData | null) {
    const collapsedLines = currentHunk
      ? getCollapsedLinesCountBetween(previousHunk, currentHunk)
      : previousHunk
        ? totalOldLines - (previousHunk.oldStart + previousHunk.oldLines) + 1
        : 0
    if (collapsedLines <= 0) return null

    const start = previousHunk ? previousHunk.oldStart + previousHunk.oldLines : 1
    const end = currentHunk ? currentHunk.oldStart : totalOldLines + 1
    const key = `gap-${previousHunk?.content ?? 'start'}-${currentHunk?.content ?? 'end'}`

    return (
      <Decoration key={key}>
        <button
          type="button"
          className="audit-diff-expand-btn"
          onClick={() => expandRange(start, end)}
        >
          {t('auditLog.drawer.expandHidden', { count: collapsedLines })}
        </button>
      </Decoration>
    )
  }

  const diffType = beforeState === null ? 'add' : afterState === null ? 'delete' : 'modify'

  return (
    <div className="audit-diff-viewer" data-testid="audit-diff-viewer">
      <Diff viewType="split" diffType={diffType} hunks={hunks} tokens={tokens}>
        {(hunksToRender) =>
          hunksToRender.reduce<React.ReactElement[]>((children, hunk, index) => {
            const previousHunk = index > 0 ? hunksToRender[index - 1] : null
            const gap = renderGap(previousHunk, hunk)
            if (gap) children.push(gap)
            children.push(<Hunk key={hunk.content} hunk={hunk} />)
            if (index === hunksToRender.length - 1) {
              const tailGap = renderGap(hunk, null)
              if (tailGap) children.push(tailGap)
            }
            return children
          }, [])
        }
      </Diff>
    </div>
  )
}
